import React, { useEffect, useState, Children, cloneElement, isValidElement } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Moon, Sun, ChevronRight, Briefcase, FileText } from 'lucide-react';
import { Modals } from './Modals';
import { ChatWidget } from './ChatWidget';
export function Layout({
  children
}: {
  children: React.ReactNode;
}) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const location = useLocation();
  const [modals, setModals] = useState({
    getStarted: false,
    resumeReview: false
  });
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDarkMode]);
  useEffect(() => {
    setMobileMenuOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);
  const navLinks = [{
    name: 'Home',
    path: '/'
  }, {
    name: 'Services',
    path: '/services'
  }, {
    name: 'About',
    path: '/about'
  }, {
    name: 'Contact',
    path: '/contact'
  }];
  return <div className="min-h-screen bg-mesh text-slate-800 dark:text-slate-200 transition-colors duration-300 font-body flex flex-col" data-id="element-37">
      {/* Navigation */}
      <header className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${isScrolled ? 'bg-white/80 dark:bg-navy-900/80 backdrop-blur-lg shadow-sm py-3' : 'bg-transparent py-5'}`} data-id="element-38">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center" data-id="element-39">
          <Link to="/" className="flex items-center group" aria-label="Hirelio Career Services home" data-id="element-40">
            <img src="https://cdn.magicpatterns.com/uploads/8YabQrVyzsQiz4gK5PGuSd/ChatGPT_Image_Jun_27,_2026,_11_11_45_AM.png" alt="Hirelio Career Services" className="h-11 w-auto object-contain dark:bg-white dark:rounded-lg dark:px-2 dark:py-1 transition-all" data-id="element-41" />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8" data-id="element-42">
            {navLinks.map(link => <Link key={link.name} to={link.path} className={`text-sm font-medium transition-colors ${location.pathname === link.path ? 'text-brand dark:text-brand-500' : 'text-slate-600 hover:text-brand dark:text-slate-300 dark:hover:text-brand-500'}`} data-id="element-43">
                {link.name}
              </Link>)}
          </nav>

          <div className="hidden md:flex items-center gap-4" data-id="element-44">
            <button onClick={() => setIsDarkMode(!isDarkMode)} className="p-2 text-slate-500 hover:text-brand dark:text-slate-400 dark:hover:text-brand-500 transition-colors rounded-full hover:bg-slate-100 dark:hover:bg-navy-800" aria-label="Toggle Dark Mode" data-id="element-45">
              {isDarkMode ? <Sun size={20} data-id="element-46" /> : <Moon size={20} data-id="element-47" />}
            </button>
            <Link to="/contact" className="bg-brand hover:bg-brand-600 text-white px-5 py-2.5 rounded-full font-medium text-sm transition-all shadow-sm hover:shadow-md active:scale-95" data-id="element-48">
              Get Started
            </Link>
          </div>

          {/* Mobile Menu Toggle */}
          <div className="flex md:hidden items-center gap-4" data-id="element-49">
            <button onClick={() => setIsDarkMode(!isDarkMode)} className="p-2 text-slate-500 dark:text-slate-400" data-id="element-50">
              {isDarkMode ? <Sun size={20} data-id="element-51" /> : <Moon size={20} data-id="element-52" />}
            </button>
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="text-slate-600 dark:text-slate-300" data-id="element-53">
              {mobileMenuOpen ? <X size={24} data-id="element-54" /> : <Menu size={24} data-id="element-55" />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      {mobileMenuOpen && <div className="fixed inset-0 z-30 bg-white dark:bg-navy-900 pt-24 px-4 pb-6 flex flex-col md:hidden" data-id="element-56">
          <nav className="flex flex-col gap-4 text-lg font-medium" data-id="element-57">
            {navLinks.map(link => <Link key={link.name} to={link.path} className={`p-3 rounded-lg ${location.pathname === link.path ? 'bg-brand-50 dark:bg-navy-800 text-brand dark:text-brand-500' : 'text-slate-700 dark:text-slate-200'}`} data-id="element-58">
                {link.name}
              </Link>)}
          </nav>
          <div className="mt-auto pt-6 border-t border-slate-100 dark:border-navy-800" data-id="element-59">
            <Link to="/contact" onClick={() => setMobileMenuOpen(false)} className="block text-center w-full bg-brand text-white py-3 rounded-xl font-medium" data-id="element-60">
              Get Started
            </Link>
          </div>
        </div>}

      {/* Main Content */}
      <main className="flex-grow pt-20" data-id="element-61">
        {Children.map(children, child => {
        if (isValidElement(child)) {
          return cloneElement(child, {
            openGetStarted: () => setModals({
              ...modals,
              getStarted: true
            }),
            openResumeReview: () => setModals({
              ...modals,
              resumeReview: true
            })
          } as any);
        }
        return child;
      })}
      </main>

      {/* Footer */}
      <footer className="bg-white/50 dark:bg-navy-900/50 backdrop-blur-md border-t border-slate-200/50 dark:border-navy-800 pt-16 pb-8" data-id="element-62">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-63">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12" data-id="element-64">
            <div data-id="element-65">
              <Link to="/" className="inline-flex items-center mb-6" aria-label="Hirelio Career Services home" data-id="element-66">
                <img src="https://cdn.magicpatterns.com/uploads/8YabQrVyzsQiz4gK5PGuSd/ChatGPT_Image_Jun_27,_2026,_11_11_45_AM.png" alt="Hirelio Career Services" className="h-12 w-auto object-contain dark:bg-white dark:rounded-lg dark:px-2 dark:py-1" data-id="element-67" />
              </Link>
              <p className="text-slate-500 dark:text-slate-400 text-sm mb-6 leading-relaxed" data-id="element-68">
                Helping professionals get hired faster with expert resume
                writing, LinkedIn optimization, and career coaching services.
              </p>
            </div>

            <div data-id="element-69">
              <h4 className="font-heading font-semibold text-navy-900 dark:text-white mb-6" data-id="element-70">
                Services
              </h4>
              <ul className="space-y-3 text-sm text-slate-500 dark:text-slate-400" data-id="element-71">
                <li data-id="element-72">
                  <Link to="/services/resume" className="hover:text-brand transition-colors" data-id="element-73">
                    Professional Resume
                  </Link>
                </li>
                <li data-id="element-74">
                  <Link to="/services/executive" className="hover:text-brand transition-colors" data-id="element-75">
                    Executive Resume
                  </Link>
                </li>
                <li data-id="element-76">
                  <Link to="/services/linkedin" className="hover:text-brand transition-colors" data-id="element-77">
                    LinkedIn Optimization
                  </Link>
                </li>
                <li data-id="element-78">
                  <Link to="/services/cover-letter" className="hover:text-brand transition-colors" data-id="element-79">
                    Cover Letter Writing
                  </Link>
                </li>
                <li data-id="element-80">
                  <Link to="/services/coaching" className="hover:text-brand transition-colors" data-id="element-81">
                    Career Coaching
                  </Link>
                </li>
              </ul>
            </div>

            <div data-id="element-82">
              <h4 className="font-heading font-semibold text-navy-900 dark:text-white mb-6" data-id="element-83">
                Company
              </h4>
              <ul className="space-y-3 text-sm text-slate-500 dark:text-slate-400" data-id="element-84">
                <li data-id="element-85">
                  <Link to="/about" className="hover:text-brand transition-colors" data-id="element-86">
                    About Us
                  </Link>
                </li>
                <li data-id="element-87">
                  <Link to="/contact" className="hover:text-brand transition-colors" data-id="element-88">
                    Contact
                  </Link>
                </li>
                <li data-id="element-89">
                  <a href="#" className="hover:text-brand transition-colors" data-id="element-90">
                    Blog
                  </a>
                </li>
                <li data-id="element-91">
                  <a href="#" className="hover:text-brand transition-colors" data-id="element-92">
                    FAQ
                  </a>
                </li>
              </ul>
            </div>

            <div data-id="element-93">
              <h4 className="font-heading font-semibold text-navy-900 dark:text-white mb-6" data-id="element-94">
                Newsletter
              </h4>
              <p className="text-slate-500 dark:text-slate-400 text-sm mb-4" data-id="element-95">
                Get career tips and job search strategies delivered to your
                inbox.
              </p>
              <form className="flex gap-2" onSubmit={e => e.preventDefault()} data-id="element-96">
                <input type="email" placeholder="Email address" className="w-full px-4 py-2 rounded-lg border border-slate-200 dark:border-navy-600 bg-white dark:bg-navy-900 text-sm focus:outline-none focus:ring-2 focus:ring-brand/50 dark:text-white" data-id="element-97" />
                <button type="submit" className="bg-brand hover:bg-brand-600 text-white px-4 py-2 rounded-lg transition-colors" data-id="element-98">
                  <ChevronRight size={20} data-id="element-99" />
                </button>
              </form>
            </div>
          </div>

          <div className="border-t border-slate-200 dark:border-navy-700 pt-8 flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-slate-500 dark:text-slate-400" data-id="element-100">
            <p data-id="element-101">
              © {new Date().getFullYear()} Hirelio Career Services. All rights
              reserved.
            </p>
            <div className="flex gap-6" data-id="element-102">
              <a href="#" className="hover:text-brand transition-colors" data-id="element-103">
                Privacy Policy
              </a>
              <a href="#" className="hover:text-brand transition-colors" data-id="element-104">
                Terms of Service
              </a>
            </div>
          </div>
        </div>
      </footer>

      <Modals getStartedOpen={modals.getStarted} resumeReviewOpen={modals.resumeReview} onClose={() => setModals({
      getStarted: false,
      resumeReview: false
    })} data-id="element-105" />

      <ChatWidget data-id="element-106" />
    </div>;
}